/**
 * @fileoverview Article render context (article template G2, decision D2)
 * @module screen-view-v2/context/ArticleRenderContext
 *
 * Provided ONLY by the web-shell engine for `metadata.kind === 'article'` documents, on the SSR render
 * and on the client re-render alike (both derive it from config.json). FlexibleLayout re-provides it
 * with the screen's heading `outline`. Every article-only renderer behaviour reads this context and
 * falls back to today's code path when it is `null` — funnels, editor canvas and previews therefore
 * render byte-identical HTML (`npm run ssr-gate`).
 */
import React from 'react';
import type { ArticlePresentation } from '@segmently/schema-models/v2';
import type { ArticleRuntimePayload } from '@modules/screen-view-v2/types/articleRuntime';

export interface ArticleOutlineHeading {
  sectionId: string;
  /** Stable slug id (from the document's defaultLanguage text). */
  id: string;
  tag: 'h2' | 'h3';
  /** Visible text in the render locale, template markup stripped. */
  text: string;
}

export interface ArticleOutline {
  headings: readonly ArticleOutlineHeading[];
}

export interface ArticleRenderContextValue {
  /** Sanitized presentation with at least one token map, else null (no style block). */
  presentation: ArticlePresentation | null;
  runtime: ArticleRuntimePayload | null;
  /** Set by FlexibleLayout for its own sections; null above it. */
  outline: ArticleOutline | null;
  defaultLanguage: string;
  /**
   * G5 (R4): the screen's first Image figure (template articles; set by FlexibleLayout) — eager + fetchpriority high;
   * every other figure lazy. Absent/null → every figure lazy.
   */
  leadFigureSectionId?: string | null;
}

export const ArticleRenderContext = React.createContext<ArticleRenderContextValue | null>(null);

ArticleRenderContext.displayName = 'ArticleRenderContext';
